import { type FormEvent, useState } from 'react';
import { authClient } from '../auth/client';

/**
 * Entry screen: sign-in and sign-up, Brazilian Portuguese, KENOCYBER visual
 * identity (black, cyan neon, discreet tech details).
 *
 * Every failure surfaces as a SAFE message: invalid credentials are generic
 * (no telling email from password, no confirming an email exists), server
 * errors never expose internals, and nothing here ever sees a token or hash -
 * the session lives in an httpOnly cookie the JS cannot read.
 */

interface AuthScreenProps {
  readonly sessionExpired: boolean;
  readonly onAuthenticated: () => void;
}

type Mode = 'sign-in' | 'sign-up';

/** Mirrors the server rule (Better Auth minPasswordLength); UX only. */
const PASSWORD_MIN = 12;
const PASSWORD_MAX = 128;

function friendlyError(status: number | undefined, code: string | undefined): string {
  if (status === 429) {
    return 'Muitas tentativas. Aguarde alguns instantes e tente novamente.';
  }
  if (code === 'INVALID_EMAIL_OR_PASSWORD') {
    return 'E-mail ou senha inválidos.';
  }
  if (code === 'USER_ALREADY_EXISTS') {
    // Kept deliberately vague-ish; sign-up is rate limited server-side.
    return 'Não foi possível criar a conta com esses dados.';
  }
  if (status !== undefined && status >= 500) {
    return 'Servidor indisponível no momento. Tente novamente em instantes.';
  }
  return 'Não foi possível concluir. Verifique os dados e tente novamente.';
}

export function AuthScreen({ sessionExpired, onAuthenticated }: AuthScreenProps) {
  const [mode, setMode] = useState<Mode>('sign-in');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState<null | 'sign-in' | 'sign-up'>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const switchMode = (next: Mode) => {
    setMode(next);
    setErrorMessage(null);
  };

  const validate = (): string | null => {
    if (mode === 'sign-up' && displayName.trim().length < 2) {
      return 'Informe um nome público com pelo menos 2 caracteres.';
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      return 'Informe um e-mail válido.';
    }
    if (password.length < PASSWORD_MIN) {
      return `A senha precisa de pelo menos ${PASSWORD_MIN} caracteres.`;
    }
    if (password.length > PASSWORD_MAX) {
      return `A senha pode ter no máximo ${PASSWORD_MAX} caracteres.`;
    }
    if (mode === 'sign-up' && password !== confirmPassword) {
      return 'As senhas não conferem.';
    }
    return null;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) {
      return;
    }
    const validation = validate();
    if (validation) {
      setErrorMessage(validation);
      return;
    }
    setErrorMessage(null);
    setBusy(mode);
    try {
      const result =
        mode === 'sign-up'
          ? // `name` is the public display name; ageVerified is not sent and
            // could not be set anyway (input:false server-side).
            await authClient.signUp.email({ name: displayName.trim(), email, password })
          : await authClient.signIn.email({ email, password });
      if (result.error) {
        setErrorMessage(friendlyError(result.error.status, result.error.code));
        return;
      }
      onAuthenticated();
    } catch {
      setErrorMessage('Servidor indisponível no momento. Tente novamente em instantes.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="auth-shell" data-testid="auth-screen">
      <div className="auth-panel">
        <h1 className="auth-title">KENOCYBER</h1>
        <p className="auth-subtitle">Acesso restrito. Identifique-se para caçar.</p>

        {sessionExpired ? (
          <p className="auth-notice" data-testid="auth-session-expired">
            Sua sessão expirou. Entre novamente.
          </p>
        ) : null}

        <div className="auth-tabs">
          <button
            type="button"
            className={mode === 'sign-in' ? 'auth-tab auth-tab-active' : 'auth-tab'}
            data-testid="auth-tab-sign-in"
            onClick={() => switchMode('sign-in')}
          >
            Entrar
          </button>
          <button
            type="button"
            className={mode === 'sign-up' ? 'auth-tab auth-tab-active' : 'auth-tab'}
            data-testid="auth-tab-sign-up"
            onClick={() => switchMode('sign-up')}
          >
            Criar conta
          </button>
        </div>

        <form className="auth-form" onSubmit={submit}>
          {mode === 'sign-up' ? (
            <label className="auth-field">
              <span>Nome público</span>
              <input
                type="text"
                data-testid="auth-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                autoComplete="nickname"
                maxLength={64}
                required
              />
            </label>
          ) : null}

          <label className="auth-field">
            <span>E-mail</span>
            <input
              type="email"
              data-testid="auth-email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
            />
          </label>

          <label className="auth-field">
            <span>Senha</span>
            <input
              type="password"
              data-testid="auth-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
              minLength={PASSWORD_MIN}
              maxLength={PASSWORD_MAX}
              required
            />
          </label>

          {mode === 'sign-up' ? (
            <label className="auth-field">
              <span>Confirmar senha</span>
              <input
                type="password"
                data-testid="auth-password-confirm"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                minLength={PASSWORD_MIN}
                maxLength={PASSWORD_MAX}
                required
              />
            </label>
          ) : null}

          {errorMessage ? (
            <p className="auth-error" role="alert" data-testid="auth-error">
              {errorMessage}
            </p>
          ) : null}

          <button
            type="submit"
            className="auth-submit"
            data-testid="auth-submit"
            disabled={busy !== null}
          >
            {busy === 'sign-up'
              ? 'Criando conta…'
              : busy === 'sign-in'
                ? 'Entrando…'
                : mode === 'sign-up'
                  ? 'Criar conta'
                  : 'Entrar'}
          </button>
        </form>

        <p className="auth-fineprint">
          Jogo para maiores de 18 anos. Verificação de idade e recuperação de senha chegam em uma
          fase futura.
        </p>
      </div>
    </div>
  );
}
